import type CMSConnector from "../connector";
import type {
  Action,
  ActionAudio,
  ActionClickout,
  ActionCustomer,
  ActionGame,
  ActionNavigation,
  ActionSocial,
  ActionVideo,
  Category,
  Name,
  NameAudio,
  NameClickout,
  NameCustomer,
  NameGame,
  NameNavigation,
  NameSocial,
  NameVideo,
  Parameter,
} from "./types";

declare global {
  interface Window {
    __cmp: any;
    dataLayer: any[];
  }
}

interface TrackingUser {
  email?: string | null;
  logged_in: boolean;
}

export class Tracking {
  private loaded = false;
  cms: CMSConnector;
  fakeDataLayer: any[] = [];
  preInitQueue: any[] = []; // Datalayer placeholder to queue events until init is called
  trackingUser: TrackingUser | null = null;
  lastUrl: URL | null = null;

  constructor(cms: CMSConnector) {
    this.cms = cms;
  }

  public init(): void {
    if (this.loaded) {
      return;
    }
    this.saveUrlParams();
    this.fakeDataLayer = []; // If gtm is not loaded we store events in an array
    // const hasState = this.restoreState();
    this.loaded = true;
    this.trackPageMeta();
    this.trackVirtualPageView({ virtual_path_change: false });
    // Push all events from the queue
    for (const event of this.preInitQueue) {
      this.pushEvent(event);
    }
    // clear the queue
    this.preInitQueue = [];
  }

  private getDataLayer(): any[] {
    if (!this.loaded) {
      console.warn(
        "Tracking not initialized, events will be queued until init is called"
      );
      return this.preInitQueue;
    }
    return window.dataLayer || this.fakeDataLayer;
  }

  public setTrackingUser(user: TrackingUser | null): void {
    this.trackingUser = user ? { ...user } : null;
  }

  /** Get the current user to include in event push */
  public getTrackingUser(): TrackingUser {
    return this.trackingUser || { logged_in: false, email: undefined };
  }

  private shouldSaveParams(params: URLSearchParams): boolean {
    const hints = ["utm_source", "utm_medium", "utm_campaign"];
    for (const hint of hints) {
      if (params.has(hint)) {
        return true;
      }
    }
    return false;
  }

  private saveUrlParams(): void {
    const key = this.paramStorageKey;
    if (!key) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    if (!this.shouldSaveParams(urlParams)) {
      return;
    }
    const blacklist = [
      // filter out login params
      "access_token",
      "refresh_token",
      "service_token",
      "service_expires_at",
      "state",
      "account_id",
      "auth_service",
    ];
    const entries = Array.from(urlParams.entries()).filter(
      ([key]) => !blacklist.includes(key)
    );
    localStorage.setItem(key, new URLSearchParams(entries).toString());
  }

  /* Get URL params for tracking redirect URLs */
  public get urlParams(): Record<string, string> {
    const key = this.paramStorageKey;
    if (!key) {
      return {};
    }
    return Object.fromEntries(
      new URLSearchParams(localStorage.getItem(key) || "").entries()
    );
  }

  private get paramStorageKey(): string | null {
    const campaignId = this.cms.campaign.id;
    if (!campaignId) {
      console.warn("CMS campaign ID not loaded");
      return null;
    }
    return `cms:tracking:params:${campaignId}`;
  }

  /** Track navigation/page load

      This is called automatically on this class init
      and should be called on route changes by the application
      for example in vue-router's afterEach hook
  */
  public trackPageMeta(): void {
    const eventData: Record<string, any> = {
      event: "page_meta",
      environment: this.cms.cmsEnvironment,
      framework: "vue", // TODO: not hardcoded, but all apps are vue atm
      user: this.getTrackingUser(),
      imprint: "umg_de", // TODO: Hardcoded, should match UMG/DG imprint and CMP ID (either umg_de or dg)
      territory: "de", // TODO: Hardcoded, is this campaign specific?
      // TODO: page_cotegory should be a field on app config
      page_category:
        this.cms.campaign.app == "newsletter-pages"
          ? "crm_signup"
          : "crm_fan_engagement",
    };
    const language = this.cms.i18n.currentLocale?.code || null;
    if (language) {
      eventData.language = language;
    }
    const partyDetails: Record<string, any> = {};
    if (this.cms.campaign.tag_manager.party_id) {
      partyDetails["artist_party_id"] = this.cms.campaign.tag_manager.party_id;
      partyDetails["brand_party_id"] = this.cms.campaign.tag_manager.party_id;
    }
    if (this.cms.campaign.tag_manager.artist) {
      partyDetails["artist_name"] = this.cms.campaign.tag_manager.artist;
    }
    if (this.cms.campaign.tag_manager.genre) {
      partyDetails["artist_genre"] = this.cms.campaign.tag_manager.genre;
    }
    eventData["party_details"] = partyDetails;

    this.pushEvent(eventData);
  }

  public trackVirtualPageView(
    params: { virtual_path_change: boolean } = { virtual_path_change: true }
  ): void {
    const currentUrl = new URL(window.location.href);
    let lastUrl: string | null = null;
    if (this.lastUrl) {
      if (this.lastUrl.origin === currentUrl.origin) {
        lastUrl = this.lastUrl.pathname + this.lastUrl.hash;
      } else {
        lastUrl = this.lastUrl.toString();
      }
    } else {
      if (document.referrer) {
        lastUrl = document.referrer;
      }
    }
    const eventData: Record<string, any> = {
      event: "virtual_pageview",
      virtual_page_path: currentUrl.pathname + currentUrl.hash,
      virtual_page_title: document.title,
      virtual_page_referrer: lastUrl || undefined,
      ...params,
    };
    this.pushEvent(eventData);
    this.lastUrl = currentUrl;
  }

  public trackRouteChange(
    params: { virtual_path_change: boolean } = { virtual_path_change: true }
  ): void {
    this.trackPageMeta();
    this.trackVirtualPageView(params);
  }

  public trackCustomer(name: NameCustomer, parameter: Parameter = {}): void {
    this.pushEvent({
      event: "Generic Event",
      event_name: name,
      [name]: { ...parameter },
    });
  }

  public trackGame(name: NameGame, parameter: Parameter = {}): void {
    this.pushEvent({
      event: "Generic Event",
      event_name: name,
      [name]: { ...parameter },
    });
  }

  public trackAudio(name: NameAudio, parameter: Parameter = {}): void {
    this.pushEvent({
      event: "Generic Event",
      event_name: name,
      [name]: { ...parameter },
    });
  }

  public trackVideo(name: NameVideo, parameter: Parameter = {}): void {
    this.pushEvent({
      event: "Generic Event",
      event_name: name,
      [name]: { ...parameter },
    });
  }

  public trackSocial(name: NameSocial, parameter: Parameter = {}): void {
    this.pushEvent({
      event: "Generic Event",
      event_name: name,
      [name]: { ...parameter },
    });
  }

  public trackNavigation(
    name: NameNavigation,
    parameter: Parameter = {}
  ): void {
    this.pushEvent({
      event: "Generic Event",
      event_name: name,
      [name]: { ...parameter },
    });
  }

  public trackClickout(name: NameClickout, parameter: Parameter = {}): void {
    this.pushEvent({
      event: "Generic Event",
      event_name: name,
      [name]: { ...parameter },
    });
  }

  pushEvent(eventData: Record<string, any>): void {
    const queued = !this.loaded ? " (queued)" : "";
    console.debug(`dataLayer push${queued}:`, eventData);
    this.getDataLayer().push(eventData);
  }
}
